import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBookChoice } from '@/contexts';
import { openNuiteeSsoInNewTab } from '@/lib/booking/openNuiteeSsoInNewTab';
import { nuiteeBookingService } from '@/services/nuiteeBookingService';
import { toast } from 'react-toastify';
import { debug } from '@/utils/logger';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_BOOKING_SSO_TAB_OPENED } from '@/lib/analyticsEvents';
import type { BookChoiceSurface } from '@/contexts/BookChoiceContext';

export interface UseTravelMenuActionsOptions {
  onBeforeNavigate?: () => void;
}

export function useTravelMenuActions(options: UseTravelMenuActionsOptions = {}) {
  const router = useNavigate();
  const { openBookChoice, isBookWhitelabelLoading } = useBookChoice();
  const [isOpeningDashboard, setIsOpeningDashboard] = useState(false);

  const closeMenus = options.onBeforeNavigate;

  const handleMyTripsClick = useCallback(() => {
    closeMenus?.();
    router('/my-travel');
  }, [closeMenus, router]);

  const handleBookClick = useCallback(
    (surface: BookChoiceSurface) => {
      closeMenus?.();
      openBookChoice(surface);
    },
    [closeMenus, openBookChoice],
  );

  const handleHotelBookingsClick = useCallback(() => {
    closeMenus?.();
    setIsOpeningDashboard(true);
    openNuiteeSsoInNewTab({
      surface: 'profile_dashboard',
      fetchLoginUrl: async () => {
        const dashboardPath = `/dashboard?clientReference=gt:NoPost:listing:place:NoPlace:ts:${Date.now()}&language=en&currency=USD`;
        const ssoResponse = await nuiteeBookingService.getSSOLink(dashboardPath);
        if (!ssoResponse.data?.loginUrl) {
          throw new Error('Missing loginUrl in SSO response');
        }
        return ssoResponse.data.loginUrl;
      },
      onSuccess: (loginUrl) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_TAB_OPENED, {
          surface: 'profile_dashboard',
          loginUrl: !!loginUrl,
          ...WebAnalytics.getReferringCreatorProps(),
        });
        setIsOpeningDashboard(false);
      },
      onBlocked: () => {
        setIsOpeningDashboard(false);
      },
      onApiError: (errorCode, errorStatus) => {
        debug('[useTravelMenuActions] Hotel bookings SSO error:', { errorCode, errorStatus });
        toast.error('Unable to open hotel bookings. Please try again.');
        setIsOpeningDashboard(false);
      },
    });
  }, [closeMenus]);

  const handleAdminClick = useCallback(() => {
    closeMenus?.();
    router('/admin');
  }, [closeMenus, router]);

  const handlePerformanceClick = useCallback(() => {
    closeMenus?.();
    router('/performance');
  }, [closeMenus, router]);

  return {
    handleMyTripsClick,
    handleBookClick,
    handleHotelBookingsClick,
    handleAdminClick,
    handlePerformanceClick,
    isOpeningDashboard,
    isBookWhitelabelLoading,
  };
}
